import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Tooltip, Typography } from '@mui/material';
import useSearchParams from '../../hooks/useSearchParams';
import { TREE_NODE, VIEW_ID } from '../../constants/urlParams';
import useSavedViews from '../../hooks/useSavedViews';
import useEnvPermission from '../../hooks/useEnvPermission';
import { FilterItem } from '../../hooks/useUrlFilters';
import { Flex } from '../Flex';
import { FilterOption } from '../FilterSelector';
import IconButton from '../IconButton';
import { ReactComponent as EditIcon } from '../../assets/16x16/edit-2.svg';
import { ReactComponent as PlusIcon } from '../../assets/16x16/plus.svg';
import { ReactComponent as EyeIcon } from '../../assets/16x16/eye.svg';
import { ReactComponent as TrashIcon } from '../../assets/16x16/delete-trash.svg';
import { ReactComponent as CopyIcon } from '../../assets/16x16/copy.svg';
import SvgIcon from '../SvgIcon';
import { MultiFilterPopover } from '../FilterPopover';
import Autocomplete from '../Autocomplete';
import Loading from '../Loading';
import Dialog from '../Dialog';
import { MultiFilter } from '../FilterPopover/MultiFilterPopover';
import { FILTER_LABELS } from '../../constants/filterLabels';

const SavedViewsContainer = styled(Flex)`
  align-items: center;
  gap: 8px;
  min-width: 0;
  justify-content: space-between;
`;

const ActionsContainer = styled(Flex)`
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const toValueOptions = (items: { value: string }[]) =>
  items.map((i) => ({ value: i.value, label: i.value }));

const formatFilters = (filters: MultiFilter[]): FilterItem[] =>
  filters
    .filter((f) => f.property && f.operator && f.value)
    .map((f) => ({
      property: f.property!,
      operator: f.operator!,
      value: f.value!,
    }));

type SavedViewOption = {
  label: string;
  value: string;
  visibility: 'private' | 'public';
  actions: { value: string; label: string; icon?: JSX.Element }[];
};

const SavedViews = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreatePopoverOpen, setIsCreatePopoverOpen] = useState(false);
  const [editingViewId, setEditingViewId] = useState<string | undefined>();
  const [deletingViewId, setDeletingViewId] = useState<string | undefined>();
  const [makingPublicViewId, setMakingPublicViewId] = useState<
    string | undefined
  >();
  const [isPrivateView, setIsPrivateView] = useState(true);
  const [isEditPrivate, setIsEditPrivate] = useState(true);

  const hasEnvPermission = useEnvPermission();
  const canEditSavedViews = useMemo(
    () => hasEnvPermission('EDIT_SAVED_VIEWS'),
    [hasEnvPermission],
  );

  const selectedViewId = useMemo(
    () => searchParams.get(VIEW_ID) || undefined,
    [searchParams],
  );

  const {
    savedViews,
    filterOptions,
    create,
    update,
    delete: deleteMutation,
  } = useSavedViews();

  const viewsById = useMemo(
    () => new Map(savedViews.data?.views.map((v) => [v.id, v])),
    [savedViews.data],
  );

  const selectedView = viewsById.get(selectedViewId ?? '');

  const selectedOption = useMemo(
    () =>
      selectedView
        ? { label: selectedView.name, value: selectedView.id }
        : null,
    [selectedView],
  );

  const savedViewOptions = useMemo(() => {
    const views = savedViews.data?.views ?? [];

    const mapped: SavedViewOption[] = [...views]
      .sort((a, b) => {
        if (a.visibility === b.visibility) return 0;
        return a.visibility === 'private' ? -1 : 1;
      })
      .map((view) => {
        const isPublic = view.visibility === 'public';
        const canModify = !isPublic || canEditSavedViews;

        const actions = [
          ...(canModify
            ? [{ value: 'edit', label: 'Edit', icon: <EditIcon /> }]
            : []),
          { value: 'duplicate', label: 'Duplicate', icon: <CopyIcon /> },
          ...(!isPublic && canEditSavedViews
            ? [
                {
                  value: 'make_public',
                  label: 'Make Public',
                  icon: (
                    <SvgIcon strokeColor="black">
                      <EyeIcon />
                    </SvgIcon>
                  ),
                },
              ]
            : []),
          ...(canModify
            ? [{ value: 'delete', label: 'Delete', icon: <TrashIcon /> }]
            : []),
        ];

        return {
          label: view.name,
          value: view.id,
          visibility: view.visibility,
          actions,
        };
      });

    return mapped;
  }, [savedViews.data, canEditSavedViews]);

  const transformedFilterOptions: FilterOption[] = useMemo(() => {
    if (!filterOptions.data) return [];

    return [
      {
        property: 'tag',
        propertyLabel: FILTER_LABELS.asset_tag,
        options: toValueOptions(filterOptions.data.tags),
      },
      {
        property: 'owner',
        propertyLabel: FILTER_LABELS.asset_owner,
        options: toValueOptions(filterOptions.data.owners),
      },
      {
        property: 'subscriber',
        propertyLabel: FILTER_LABELS.asset_subscriber,
        options: toValueOptions(filterOptions.data.subscribers),
      },
      {
        property: 'name',
        propertyLabel: FILTER_LABELS.asset_name,
        options: toValueOptions(filterOptions.data.names),
      },
      {
        property: 'path',
        propertyLabel: FILTER_LABELS.asset_path,
        options: toValueOptions(filterOptions.data.paths),
      },
      {
        property: 'is_critical',
        propertyLabel: FILTER_LABELS.is_critical,
        options: filterOptions.data.isCritical.map((c) => ({
          value: c.value,
          label: c.value === 'true' ? 'Critical' : 'Not Critical',
        })),
        isSingularValue: true,
      },
      {
        property: 'database_name',
        propertyLabel: FILTER_LABELS.database_name,
        options: toValueOptions(filterOptions.data.databaseNames),
      },
      {
        property: 'schema_name',
        propertyLabel: FILTER_LABELS.schema_name,
        options: toValueOptions(filterOptions.data.schemaNames),
      },
      {
        property: 'table_name',
        propertyLabel: FILTER_LABELS.table_name,
        options: toValueOptions(filterOptions.data.tableNames),
      },
    ];
  }, [filterOptions.data]);

  const handleViewSelect = useCallback(
    (viewId: string | undefined) => {
      setSearchParams((prev) => {
        prev.delete(TREE_NODE);
        if (viewId) {
          prev.set(VIEW_ID, viewId);
        } else {
          prev.delete(VIEW_ID);
        }
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleCreateView = useCallback(
    (title: string, filters: MultiFilter[]) => {
      create.mutate(
        {
          name: title,
          filters: formatFilters(filters),
          visibility: isPrivateView ? 'private' : 'public',
        },
        {
          onSuccess: (newView) => {
            handleViewSelect(newView.id);
            setIsPrivateView(true);
          },
        },
      );
    },
    [create, handleViewSelect, isPrivateView],
  );

  const handleUpdateView = useCallback(
    (title: string, filters: MultiFilter[]) => {
      if (!editingViewId) return;

      update.mutate({
        savedViewId: editingViewId,
        data: {
          name: title,
          filters: formatFilters(filters),
          ...(!isEditPrivate ? { visibility: 'public' } : {}),
        },
      });
    },
    [editingViewId, isEditPrivate, update],
  );

  const editingView = editingViewId ? viewsById.get(editingViewId) : undefined;

  const handleEditPopoverChange = useCallback((open: boolean) => {
    if (!open) {
      setEditingViewId(undefined);
      setIsEditPrivate(true);
    }
  }, []);

  const handleCreatePopoverChange = useCallback((open: boolean) => {
    if (!open) {
      setIsPrivateView(true);
    }
    setIsCreatePopoverOpen(open);
  }, []);

  const handleDeleteView = useCallback(
    (viewId: string) => {
      deleteMutation.mutate(viewId, {
        onSuccess: () => {
          if (selectedViewId === viewId) {
            handleViewSelect(undefined);
          }
        },
      });
    },
    [deleteMutation, selectedViewId, handleViewSelect],
  );

  const handleDuplicateView = useCallback(
    (viewId: string) => {
      const view = viewsById.get(viewId);
      if (!view) return;

      create.mutate(
        {
          name: `Copy of ${view.name}`,
          filters: view.filters,
        },
        {
          onSuccess: (newView) => {
            handleViewSelect(newView.id);
          },
        },
      );
    },
    [viewsById, create, handleViewSelect],
  );

  const handleConfirmMakePublic = useCallback(() => {
    if (!makingPublicViewId) return;
    update.mutate(
      { savedViewId: makingPublicViewId, data: { visibility: 'public' } },
      { onSuccess: () => setMakingPublicViewId(undefined) },
    );
  }, [makingPublicViewId, update]);

  const handleAction = useCallback(
    (optionValue: string, actionValue: string) => {
      switch (actionValue) {
        case 'delete':
          setDeletingViewId(optionValue);
          break;
        case 'edit':
          setEditingViewId(optionValue);
          break;
        case 'duplicate':
          handleDuplicateView(optionValue);
          break;
        case 'make_public':
          setMakingPublicViewId(optionValue);
          break;
      }
    },
    [handleDuplicateView],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!deletingViewId) return;
    handleDeleteView(deletingViewId);
    setDeletingViewId(undefined);
  }, [deletingViewId, handleDeleteView]);

  const isLoading = useMemo(
    () => create.isLoading || update.isLoading || deleteMutation.isLoading,
    [create.isLoading, update.isLoading, deleteMutation.isLoading],
  );

  const deletingView = deletingViewId
    ? viewsById.get(deletingViewId)
    : undefined;
  const makingPublicView = makingPublicViewId
    ? viewsById.get(makingPublicViewId)
    : undefined;

  const isEditDisabled = useMemo(
    () =>
      !selectedViewId ||
      isLoading ||
      (selectedView?.visibility === 'public' && !canEditSavedViews),
    [selectedViewId, isLoading, selectedView, canEditSavedViews],
  );

  return (
    <>
      <SavedViewsContainer>
        <Autocomplete
          variant="borderless"
          listboxMaxWidth="350px"
          options={savedViewOptions}
          value={selectedOption}
          onChange={(_, val) => {
            handleViewSelect(val?.value);
          }}
          onOptionAction={handleAction}
          actions={['+ Create new']}
          onAction={() => setIsCreatePopoverOpen(true)}
          placeholder="Saved Views"
          disableClearable
          chipDisplay
          showEmptyMessage={false}
          groupBy={(option) =>
            option.visibility === 'public' ? 'Public' : 'Private'
          }
          groups={['Private', 'Public']}
          groupEmptyText="No Saved Views"
        />

        {isLoading && <Loading size={14} />}

        <ActionsContainer>
          <MultiFilterPopover
            button={
              <Tooltip title="Edit view">
                <IconButton
                  disabled={isEditDisabled}
                  onClick={() => setEditingViewId(selectedViewId)}
                >
                  <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
                    <EditIcon />
                    {selectedView && (
                      <Typography sx={{ color: 'common.gray2' }}>{`(${
                        selectedView.filters?.length || 0
                      })`}</Typography>
                    )}
                  </Flex>
                </IconButton>
              </Tooltip>
            }
            filterOptions={transformedFilterOptions}
            sortOptions={false}
            onSave={handleUpdateView}
            initialTitle={editingView?.name}
            initialFilters={editingView?.filters}
            saveButtonText="Update view"
            isOpen={!!editingViewId}
            onOpenChange={handleEditPopoverChange}
            checkboxOption={
              canEditSavedViews && editingView?.visibility === 'private'
                ? {
                    label: 'Private',
                    checked: isEditPrivate,
                    onChange: setIsEditPrivate,
                  }
                : undefined
            }
          />

          <MultiFilterPopover
            button={
              <Tooltip title="Create new view">
                <IconButton disabled={isLoading}>
                  <PlusIcon />
                </IconButton>
              </Tooltip>
            }
            filterOptions={transformedFilterOptions}
            sortOptions={false}
            onSave={handleCreateView}
            saveButtonText="Save as a new view"
            isOpen={isCreatePopoverOpen}
            onOpenChange={handleCreatePopoverChange}
            checkboxOption={
              canEditSavedViews
                ? {
                    label: 'Private',
                    checked: isPrivateView,
                    onChange: setIsPrivateView,
                  }
                : undefined
            }
          />
        </ActionsContainer>
      </SavedViewsContainer>

      <Dialog
        open={!!deletingViewId}
        onClose={() => setDeletingViewId(undefined)}
        onConfirmButtonClick={handleConfirmDelete}
        isLoading={deleteMutation.isLoading}
        title="Delete saved view"
        message={`Are you sure you want to delete "${deletingView?.name}"?`}
        confirmText="Delete"
        variant="error"
      />

      <Dialog
        open={!!makingPublicViewId}
        onClose={() => setMakingPublicViewId(undefined)}
        onConfirmButtonClick={handleConfirmMakePublic}
        isLoading={update.isLoading}
        title="Make view public"
        message={`Are you sure you want to make "${makingPublicView?.name}" public? This cannot be undone — public views are visible to everyone in the organization.`}
        confirmText="Make Public"
        customWidth={400}
      />
    </>
  );
};

export default SavedViews;
